/* Multiple choice: tap an option (or press 1-9). Wrong picks can be compared with the answer. */
import { useEffect, useRef, useState } from 'react';
import { PlayButton } from '../../../ui/Button';
import { Rich } from '../../../ui/Rich';
import type { ChoiceOption, ChoiceQuestion, QuestionApi } from '../types';

export function ChoiceArea({ q, api }: { q: ChoiceQuestion; api: QuestionApi }) {
  const maxMiss = q.maxMiss || (api.mode === 'challenge' ? 1 : q.options.length <= 3 ? 1 : 2);
  const [state, setState] = useState<Record<string, 'ok' | 'bad' | 'reveal'>>({});
  // the compare row reflects the moment of the last wrong pick
  const [chosen, setChosen] = useState<{ opt: ChoiceOption; ended: boolean } | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    api.setMaxMiss(maxMiss);
  }, [api, maxMiss]);

  const pick = (opt: ChoiceOption) => {
    if (api.isDone() || state[String(opt.id)] === 'bad') return;
    if (opt.id === q.answer) {
      setState((s) => ({ ...s, [String(opt.id)]: 'ok' }));
      api.done(true);
      return;
    }
    setState((s) => ({ ...s, [String(opt.id)]: 'bad' }));
    api.miss({ chosen: opt.id });
    if (api.isDone()) setState((s) => ({ ...s, [String(q.answer)]: 'reveal' }));
    else api.feedback(q.retryText || 'Olmadı. Tekrar dinleyip yeniden dene.', 'bad');
    setChosen({ opt, ended: api.isDone() });
    setTick((t) => t + 1);
  };
  const pickRef = useRef(pick);
  pickRef.current = pick;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /input|textarea|select/i.test(t.tagName)) return;
      const n = Number.parseInt(e.key, 10);
      const opt = q.options[n - 1];
      if (n >= 1 && opt) pickRef.current(opt);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [q]);

  const cls = `choices${q.options.length > 6 ? ' many' : ''}${q.columns ? ` cols-${q.columns}` : ''}`;
  const preview = q.preview;
  return (
    <>
      <div className={cls}>
        {q.options.map((opt, i) => {
          const s = state[String(opt.id)];
          return (
            <button
              key={String(opt.id)}
              type="button"
              className={`choice${s === 'ok' ? ' ok' : s === 'bad' ? ' bad' : s === 'reveal' ? ' ok reveal' : ''}`}
              data-k={i + 1}
              disabled={s === 'bad'}
              onClick={() => pick(opt)}
            >
              <span className="c-main">
                <Rich text={opt.label} />
              </span>
              {opt.sub ? (
                <span className="c-sub">
                  <Rich text={opt.sub} />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="compare-row">
        {preview && chosen ? (
          <>
            <span className="cmp-label">Karşılaştır:</span>
            <PlayButton label="Seçtiğin" rich={false} start={() => preview(chosen.opt.id)} />
            {chosen.ended ? <PlayButton label="Doğrusu" rich={false} start={() => preview(q.answer)} /> : null}
          </>
        ) : null}
      </div>
    </>
  );
}
